/*
 * STRAP CUSTOMISATION (ProvenGK custom — added Aug 2026, see build spec)
 *
 * Three responsibilities:
 *
 * 1. PDP: when the strap customisation toggle (snippets/strap-customisation.liquid)
 *    is ticked, intercept the add-to-cart and POST glove + charge lines in a
 *    single /cart/add.js request, linked by a hidden _pgk_link property. Untouched
 *    forms keep the theme's native ProductForm flow.
 *
 * 2. Cart reconciliation, on load and on every cart change:
 *    - charge line with no matching glove (same _pgk_link) -> removed
 *    - charge quantity differs from its glove              -> synced
 *    - glove still customised but charge removed manually  -> properties stripped
 *      (so nobody gets a free print)
 *
 * 3. Cart prompt (snippets/strap-customisation-prompt.liquid): attaches
 *    customisation to an eligible glove already in the cart.
 */
(function () {
  'use strict';

  function chargeVariantId() {
    var el = document.querySelector('[data-strap-customisation]');
    return el ? parseInt(el.getAttribute('data-charge-variant-id'), 10) : 56035845767552;
  }
  var CHARGE_VARIANT_ID = 56035845767552;

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  /* ------------------------------------------------------------------ */
  /* PDP toggle                                                          */
  /* ------------------------------------------------------------------ */

  function initBlocks() {
    document.querySelectorAll('[data-strap-customisation]').forEach(function (block) {
      if (block.dataset.strapInit) return;
      block.dataset.strapInit = '1';

      var toggle = block.querySelector('[data-strap-toggle]');
      var input = block.querySelector('[data-strap-text]');
      var fields = block.querySelector('.strap-customisation__fields');
      var error = block.querySelector('[data-strap-error]');
      var form = block.closest('form');

      if (!toggle || !input || !fields) return;

      toggle.addEventListener('change', function () {
        if (toggle.checked) {
          fields.hidden = false;
          input.disabled = false;
          input.focus();
        } else {
          // Hide, clear AND disable — a hidden-but-enabled input still posts.
          fields.hidden = true;
          input.value = '';
          input.disabled = true;
          if (error) error.hidden = true;
        }
        // Buy-it-now / Shop Pay would skip the cart and lose the charge line,
        // so hide the dynamic checkout button while customising.
        if (form) {
          var paymentButton = form.querySelector('.shopify-payment-button');
          if (paymentButton) paymentButton.style.display = toggle.checked ? 'none' : '';
        }
      });

      input.addEventListener('input', function () {
        if (error && input.value.trim()) error.hidden = true;
      });
    });
  }

  // Validate and run our combined add for a form whose toggle is ticked.
  function processOwnedSubmit(form, block) {
    var input = block.querySelector('[data-strap-text]');
    var error = block.querySelector('[data-strap-error]');
    var text = (input && input.value ? input.value : '').trim();

    if (!text) {
      if (error) error.hidden = false;
      if (input) input.focus();
      return;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    addGloveWithCustomisation(form, block, text);
  }

  // Returns the strap block when this event belongs to a product form with the
  // toggle ticked — the case where we own the add-to-cart entirely.
  function ownedBlock(form) {
    if (!(form instanceof HTMLFormElement)) return null;
    var block = form.querySelector('[data-strap-customisation]');
    if (!block) return null;
    var toggle = block.querySelector('[data-strap-toggle]');
    return toggle && toggle.checked ? block : null;
  }

  // Window-level CAPTURE click listener: window capture runs before any
  // document- or element-level handler, which matters because cart upsell apps
  // (e.g. Vitals volume discounts) hijack the add-to-cart CLICK and add the
  // item through their own AJAX — the form submit never fires, and a
  // submit-level intercept alone would be bypassed, losing the customisation.
  window.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    var button = target && target.closest('button, input[type="submit"]');
    if (!button || button.type !== 'submit' || !button.form) return;

    var block = ownedBlock(button.form);
    if (!block) return; // native flow, apps included

    event.preventDefault();
    event.stopImmediatePropagation();
    processOwnedSubmit(button.form, block);
  }, true);

  // Fallback for non-click submissions (Enter key in a field, etc.). Document
  // capture fires before the theme's ProductForm submit handler on the form.
  document.addEventListener('submit', function (event) {
    var block = ownedBlock(event.target);
    if (!block) return; // native flow

    event.preventDefault();
    event.stopPropagation();
    processOwnedSubmit(event.target, block);
  }, true);

  function formControl(form, name) {
    // form.elements includes controls linked via the form="" attribute
    // (Impact renders the quantity selector outside the <form>).
    var control = form.elements.namedItem(name);
    if (control && typeof RadioNodeList !== 'undefined' && control instanceof RadioNodeList) {
      return control[0];
    }
    return control;
  }

  function addGloveWithCustomisation(form, block, text) {
    var idControl = formControl(form, 'id');
    var qtyControl = formControl(form, 'quantity');
    var variantId = idControl ? parseInt(idControl.value, 10) : NaN;
    var quantity = qtyControl ? parseInt(qtyControl.value, 10) || 1 : 1;

    if (!variantId) return;

    var submitButtons = Array.prototype.filter.call(form.elements, function (el) {
      return el.type === 'submit';
    });
    submitButtons.forEach(function (button) { button.setAttribute('aria-busy', 'true'); });

    var linkId = newLinkId();

    // Mirror the theme's ProductForm: bundle the variant-added section so the
    // confirmation drawer renders, and let other components register sections.
    var sectionsToBundle = ['variant-added'];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    var payload = {
      items: [
        {
          id: variantId,
          quantity: quantity,
          properties: { 'Customisation': text, '_pgk_link': linkId }
        },
        {
          id: chargeVariantId(),
          quantity: quantity,
          properties: { '_pgk_link': linkId }
        }
      ],
      sections: sectionsToBundle.join(','),
      sections_url: root() + 'variants/' + variantId
    };

    fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      return response.json().then(function (json) { return { ok: response.ok, json: json }; });
    }).then(function (result) {
      submitButtons.forEach(function (button) { button.removeAttribute('aria-busy'); });

      if (!result.ok) {
        form.dispatchEvent(new CustomEvent('cart:error', {
          bubbles: true,
          detail: { error: result.json.description || result.json.message || 'Could not add to cart' }
        }));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return;
      }

      var settings = (window.themeVariables && window.themeVariables.settings) || {};
      if (settings.cartType === 'page' || settings.pageType === 'cart') {
        window.location.href = root() + 'cart';
        return;
      }

      return fetch(root() + 'cart.js').then(function (r) { return r.json(); }).then(function (cartContent) {
        cartContent.sections = result.json.sections;
        var items = result.json.hasOwnProperty('items') ? result.json.items : [result.json];
        form.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: { items: items, cart: cartContent }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cartContent }
        }));
      });
    }).catch(function () {
      submitButtons.forEach(function (button) { button.removeAttribute('aria-busy'); });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Cart reconciliation                                                 */
  /* ------------------------------------------------------------------ */

  var reconciling = false;

  function reconcileCart() {
    if (reconciling) return;
    reconciling = true;

    fetch(root() + 'cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      var gloveByLink = {};
      cart.items.forEach(function (item) {
        var link = item.properties && item.properties._pgk_link;
        if (link && item.variant_id !== CHARGE_VARIANT_ID) gloveByLink[link] = item;
      });

      var quantityUpdates = {}; // line key -> new quantity (0 = remove)
      var liveChargeLinks = {};

      cart.items.forEach(function (item) {
        if (item.variant_id !== CHARGE_VARIANT_ID) return;
        var link = item.properties && item.properties._pgk_link;
        var glove = link && gloveByLink[link];

        if (!glove) {
          quantityUpdates[item.key] = 0; // orphaned charge — glove was removed
        } else {
          liveChargeLinks[link] = true;
          if (item.quantity !== glove.quantity) quantityUpdates[item.key] = glove.quantity;
        }
      });

      // Glove still carries customisation but its charge line was removed by
      // hand: treat that as opting out and strip the properties.
      var propertyStrips = [];
      Object.keys(gloveByLink).forEach(function (link) {
        if (!liveChargeLinks[link]) {
          propertyStrips.push(gloveByLink[link]);
        }
      });

      var hasUpdates = Object.keys(quantityUpdates).length > 0;
      var chain = Promise.resolve();

      if (hasUpdates) {
        chain = chain.then(function () {
          return fetch(root() + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: quantityUpdates })
          });
        });
      }

      propertyStrips.forEach(function (glove) {
        // /cart/change.js silently ignores an empty properties object, so the
        // only way to strip properties is to remove the line and re-add the
        // variant plain (which also merges with any uncustomised line).
        chain = chain.then(function () {
          return fetch(root() + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: glove.key, quantity: 0 })
          });
        }).then(function () {
          return fetch(root() + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: [{ id: glove.variant_id, quantity: glove.quantity }] })
          });
        });
      });

      return chain.then(function () {
        if (hasUpdates || propertyStrips.length) {
          reconciling = false;
          var settings = (window.themeVariables && window.themeVariables.settings) || {};
          if (settings.pageType === 'cart') {
            window.location.reload();
          } else {
            // Re-renders the drawer; the follow-up reconcile pass finds a clean
            // cart and goes quiet, so this cannot loop.
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }
        }
      });
    }).catch(function () { /* transient network failure — next cart event retries */ })
      .finally(function () { reconciling = false; });
  }

  /* ------------------------------------------------------------------ */
  /* Cart prompt (Phase 2)                                               */
  /* ------------------------------------------------------------------ */

  function newLinkId() {
    return (window.crypto && crypto.randomUUID)
      ? crypto.randomUUID()
      : 'pgk-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
  }

  // The prompt (snippets/strap-customisation-prompt.liquid) can be re-rendered
  // with its section, so it is wired with delegated listeners rather than
  // per-element ones.
  document.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    var opener = target.closest('[data-strap-prompt-open]');
    if (opener) {
      var openBox = opener.closest('[data-strap-drawer-prompt]');
      var form = openBox.querySelector('[data-strap-prompt-form]');
      form.hidden = !form.hidden;
      if (!form.hidden) openBox.querySelector('[data-strap-prompt-text]').focus();
      return;
    }

    var addButton = target.closest('[data-strap-prompt-add]');
    if (!addButton || addButton.disabled) return;

    var box = addButton.closest('[data-strap-drawer-prompt]');
    var input = box.querySelector('[data-strap-prompt-text]');
    var text = (input.value || '').trim();
    if (!text) { input.focus(); return; }

    addButton.disabled = true;
    addButton.setAttribute('aria-busy', 'true');

    var lineKey = box.getAttribute('data-line-key');
    var quantity = parseInt(box.getAttribute('data-line-qty'), 10) || 1;
    var chargeId = parseInt(box.getAttribute('data-charge-variant-id'), 10);
    var linkId = newLinkId();

    // Attach the properties to the glove line already in the cart, then add
    // the matching charge line.
    fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity, properties: { 'Customisation': text, '_pgk_link': linkId } })
    }).then(function (response) {
      if (!response.ok) throw new Error('cart/change failed');
      return fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: chargeId, quantity: quantity, properties: { '_pgk_link': linkId } }] })
      });
    }).then(function () {
      var settings = (window.themeVariables && window.themeVariables.settings) || {};
      if (settings.pageType === 'cart') {
        // The cart page is server-rendered — reload to show the new lines.
        window.location.reload();
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    }).catch(function () {
      addButton.disabled = false;
      addButton.removeAttribute('aria-busy');
    });
  });

  // Enter inside the prompt's text box triggers the add (it lives outside any
  // form, so the browser does nothing with Enter by default).
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    var target = event.target instanceof Element ? event.target : null;
    if (!target || !target.hasAttribute('data-strap-prompt-text')) return;
    event.preventDefault();
    var box = target.closest('[data-strap-drawer-prompt]');
    var addButton = box && box.querySelector('[data-strap-prompt-add]');
    if (addButton) addButton.click();
  });

  /* ------------------------------------------------------------------ */
  /* Wiring                                                              */
  /* ------------------------------------------------------------------ */

  function init() {
    initBlocks();
    reconcileCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', initBlocks);
  document.addEventListener('cart:change', function () { reconcileCart(); });
  document.addEventListener('cart:refresh', function () { reconcileCart(); });
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) reconcileCart();
  });
})();
